import { useEffect, useState } from "react";
import { Target, TrendingUp, PiggyBank, Wallet } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const LIGHT = "#C7BFFB";
const frMonth = (key) => new Date(`${key}-15`).toLocaleDateString("fr-FR", { month: "short" });

function Progress({ label, current, target, pct, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-neutral-500">{label}</span>
        <span className="font-medium text-neutral-700">{eur(current)}{target ? ` / ${eur(target)}` : ""}</span>
      </div>
      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct ?? 0)}%`, background: color }} />
      </div>
      {target ? <p className="text-[10px] mt-0.5 text-right font-medium text-neutral-400">{pct} %</p>
        : <p className="text-[10px] mt-0.5 text-right text-neutral-300">objectif non défini</p>}
    </div>
  );
}

export default function ForecastTab({ year }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ ca_annuel: "", epargne: "" });
  const load = () => api.get("/pilotage/forecast", { params: { year } }).then((r) => {
    setData(r.data);
    setF({ ca_annuel: r.data.objectifs?.ca_annuel || "", epargne: r.data.objectifs?.epargne || "" });
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setData(null); load(); }, [year]); // eslint-disable-line
  if (!data) return <p className="py-10 text-center text-xs text-neutral-400">Chargement…</p>;
  const { projection_year_end: proj, totals_realized: tot, monthly_average: avg, objectifs: obj } = data;

  const save = async () => {
    try {
      await api.put("/pilotage/settings", {
        objectifs: {
          ca_mensuel: +obj.ca_mensuel || 0, reste_a_vivre: +obj.reste_a_vivre || 0,
          budget_pro: +obj.budget_pro || 0, budget_perso: +obj.budget_perso || 0,
          ca_annuel: +f.ca_annuel || 0, epargne: +f.epargne || 0,
        },
      });
      toast.success("Objectifs annuels enregistrés"); setEdit(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const chartData = data.months.map((m) => ({
    key: m.key,
    realise: m.projected ? null : m.encaisse,
    projete: m.projected ? m.encaisse : null,
    reste: m.reste_a_vivre,
  }));
  const cards = [
    [`CA réalisé ${year} à ce jour`, eur(tot.encaisse), `${data.elapsed_months} mois écoulés`, TrendingUp, "#E7F6EC", "#16A34A", "forecast-realise"],
    ["Moyenne mensuelle", eur(avg.encaisse), "base de la projection", Wallet, "#F1EFFE", VIOLET, "forecast-average"],
    ["Projection CA fin d'année", eur(proj.encaisse), "moyenne extrapolée à décembre", Target, "#E8F0FE", "#1D4ED8", "forecast-projection"],
    ["Reste à vivre projeté", eur(proj.reste_a_vivre), "cumul annuel estimé", PiggyBank, "#FDEAF3", "#DB2777", "forecast-reste"],
  ];
  return (
    <div className="space-y-4" data-testid="forecast-tab">
      {data.elapsed_months === 0 && (
        <p className="text-xs text-neutral-400 bg-neutral-50 border border-neutral-100 rounded-xl px-4 py-3" data-testid="forecast-no-data">
          Aucun mois écoulé sur {year} — la projection s'affichera dès les premiers encaissements de l'année.
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([l, v, sub, Icon, bg, fg, tid]) => (
          <div key={tid} className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={tid}>
            <div className="flex items-center justify-between mb-2">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg, color: fg }}><Icon className="w-4 h-4" /></span>
            </div>
            <p className="text-[11px] text-neutral-400 font-medium">{l}</p>
            <p className="text-xl font-bold text-[#0B132B] mt-0.5">{v}</p>
            <p className="text-[10px] text-neutral-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="forecast-chart">
          <h3 className="text-sm font-semibold text-[#0B132B] mb-2">Réalisé vs projeté — {year}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ left: -14, right: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="key" tickFormatter={frMonth} tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
                <RTooltip formatter={(v, n) => [eur(v), { realise: "CA réalisé", projete: "CA projeté", reste: "Reste à vivre" }[n] || n]}
                  labelFormatter={(k) => new Date(`${k}-15`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} />
                <Bar dataKey="realise" fill={VIOLET} radius={[3, 3, 0, 0]} maxBarSize={16} />
                <Bar dataKey="projete" fill={LIGHT} radius={[3, 3, 0, 0]} maxBarSize={16} />
                <Line dataKey="reste" stroke="#16A34A" strokeWidth={2} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: VIOLET }} /> Réalisé</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: LIGHT }} /> Projeté (moyenne des mois écoulés)</span>
          </p>
        </div>
        <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="forecast-objectifs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B132B] flex items-center gap-1.5"><Target className="w-4 h-4" style={{ color: VIOLET }} />Objectifs annuels {year}</h3>
            <button onClick={() => setEdit(!edit)} className="text-[11px] font-medium hover:underline" style={{ color: VIOLET }} data-testid="forecast-objectifs-edit">{edit ? "Annuler" : "Modifier"}</button>
          </div>
          {edit ? (
            <div className="space-y-2">
              <label className="flex items-center justify-between gap-2 text-xs text-neutral-500">Objectif CA annuel
                <Input type="number" min="0" value={f.ca_annuel} onChange={(e) => setF({ ...f, ca_annuel: e.target.value })} className="w-28 h-8 text-xs" data-testid="forecast-obj-ca" /></label>
              <label className="flex items-center justify-between gap-2 text-xs text-neutral-500">Objectif épargne (reste à vivre)
                <Input type="number" min="0" value={f.epargne} onChange={(e) => setF({ ...f, epargne: e.target.value })} className="w-28 h-8 text-xs" data-testid="forecast-obj-epargne" /></label>
              <Button onClick={save} className="w-full h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="forecast-obj-save">Enregistrer</Button>
              <p className="text-[10px] text-neutral-400">Les objectifs mensuels se règlent dans l'onglet Vue d'ensemble.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <Progress label="CA annuel (projection)" current={proj.encaisse} target={obj.ca_annuel} pct={data.ca_annuel_pct} color={VIOLET} testid="forecast-progress-ca" />
              <Progress label="Épargne / reste à vivre (projection)" current={proj.reste_a_vivre} target={obj.epargne} pct={data.epargne_pct} color="#16A34A" testid="forecast-progress-epargne" />
              <div className="rounded-xl p-3 text-[11px] mt-2" style={{ background: "#F1EFFE" }}>
                <p className="font-semibold text-[#0B132B]">Résultat pro projeté</p>
                <p className="text-lg font-bold mt-0.5" style={{ color: VIOLET }}>{eur(proj.resultat_pro)}</p>
                <p className="text-neutral-400 mt-0.5">après dépenses pro, URSSAF et impôts provisionnés</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
